import type { ThemeConfig } from 'antd-v5';
import vars from 'scouting-ui/lib/variables';

const stripUnit = (value: string = '') => +value.replace('px', '');

/** Transformed vars since antd requires some as numbers  */
const tVars = {
  'border-radius': stripUnit(vars['border-radius']),
  'border-radius-sm': stripUnit(vars['border-radius-sm']),

  'button-height': stripUnit(vars['button-height']),
  'button-height-lg': stripUnit(vars['button-height-lg']),
  'button-height-sm': stripUnit(vars['button-height-sm']),

  'checkbox-size': stripUnit(vars['checkbox-size']),

  'input-height': stripUnit(vars['input-height']),
  'input-height-sm': stripUnit(vars['input-height-sm']),

  'spacing-xs': stripUnit(vars['spacing-xs']),
  'spacing-sm': stripUnit(vars['spacing-sm']),
  'spacing-1': stripUnit(vars['spacing-1']),
  'spacing-2': stripUnit(vars['spacing-2']),
  'spacing-3': stripUnit(vars['spacing-3']),
  'spacing-4': stripUnit(vars['spacing-4']),
  'spacing-5': stripUnit(vars['spacing-5']),
  'spacing-6': stripUnit(vars['spacing-6']),
  'spacing-7': stripUnit(vars['spacing-7']),
  'spacing-8': stripUnit(vars['spacing-8']),
  'spacing-9': stripUnit(vars['spacing-9']),
  'spacing-10': stripUnit(vars['spacing-10']),
  'spacing-11': stripUnit(vars['spacing-11']),
  'spacing-12': stripUnit(vars['spacing-12']),
  'spacing-13': stripUnit(vars['spacing-13']),
  'spacing-14': stripUnit(vars['spacing-14']),
};

const sblTheme: ThemeConfig = {
  cssVar: { key: 'sbl-theme' },
  hashed: false,
  token: {
    borderRadius: tVars['border-radius'],
    marginXXS: tVars['spacing-1'],
    marginXS: tVars['spacing-2'],
    marginSM: tVars['spacing-3'],
    marginMD: tVars['spacing-5'],
    marginLG: tVars['spacing-7'],
    marginXL: tVars['spacing-8'],
    marginXXL: tVars['spacing-9'],
    fontFamily: vars['font-primary'],
    red: vars['color-red'],
    green: vars['color-green'],
    blue: vars['color-blue'],
    orange: vars['color-orange'],
    colorError: vars['color-error'],
    colorSuccess: vars['color-success'],
    colorWarning: vars['color-warning'],
  },

  components: {
    Typography: {
      colorTextHeading: vars['color-text-heading'],
      colorText: vars['color-text-regular'],
      fontSizeHeading1: 64,
      fontSizeHeading2: 40,
      fontSizeHeading3: 32,
      fontSizeHeading4: 24,
      fontSizeHeading5: 16,
      lineHeightHeading1: 1.5,
      lineHeightHeading2: 1.4,
      lineHeightHeading3: 1.5,
      lineHeightHeading4: 1.33,
      lineHeightHeading5: 1.5,
      titleMarginBottom: 0,
      margin: 0,
    },

    Button: {
      fontSize: 14,
      fontSizeSM: 12,
      fontSizeLG: 16,
      fontSizeXL: 16,
      fontFamily: vars['font-serif'],
      fontWeight: 700,
      lineHeightLG: 1.4,

      colorBgContainerDisabled: vars['color-primary-white'],
      colorPrimary: vars['color-primary'],
      colorPrimaryHover: vars['color-primary-hover'],
      colorPrimaryActive: vars['color-primary-active'],
      colorTextDisabled: vars['color-text-disabled'],
      controlHeight: tVars['button-height'],
      controlHeightLG: tVars['button-height-lg'],
      controlHeightSM: tVars['button-height-sm'],
      defaultBorderColor: vars['color-primary-blueLink'],
      defaultColor: vars['color-primary-blueLink'],
      defaultHoverBorderColor: vars['color-primary'],
      defaultHoverColor: vars['color-primary'],
      paddingInlineSM: 16,
      primaryShadow: 'none',
      ...{ blueShadowColor: 'none' },
      // button 'text' variant
      textTextColor: vars['color-primary-blueLink'],
      textTextHoverColor: vars['color-primary'],
    },

    Input: {
      activeBg: vars['input-active-bg'],
      activeBorderColor: vars['color-primary-blueLink'],
      activeShadow: '',
      borderRadius: tVars['border-radius'],
      borderRadiusSM: tVars['border-radius'],
      colorIcon: vars['color-primary'],
      controlHeight: tVars['input-height'],
      controlHeightSM: tVars['input-height-sm'],
      hoverBorderColor: vars['color-blue'],
      paddingBlock: 16,
      paddingInline: 16,
      paddingInlineSM: 16,
    },

    InputNumber: {
      controlHeight: tVars['input-height'],
      borderRadius: tVars['border-radius'],
    },

    Checkbox: {
      colorPrimary: vars['color-blue'],
      controlInteractiveSize: tVars['checkbox-size'],
      borderRadiusSM: 6,
      lineWidth: 2,
      colorBorder: vars['color-blue'],
      colorBgContainerDisabled: 'white',
      ...({
        lineHeight: vars['checkbox-size'],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any),
    },

    Form: {
      labelHeight: tVars['input-height'],
    },

    Modal: {
      borderRadiusLG: 16,
      titleColor: vars['color-primary'],
      ...{
        footerMarginTop: vars['spacing-6'],
        contentPadding: 40,
      }, // needed to fix typechking, props not typed but they work
    },

    Mentions: {
      paddingBlock: 16,
      borderRadius: tVars['border-radius'],
    },

    Select: {
      controlHeight: tVars['input-height'],
      borderRadius: tVars['border-radius'],
      paddingSM: 17,
    },

    Dropdown: {
      paddingBlock: 8,
      controlItemBgHover: vars['color-item-hover-bg'],
    },

    Popconfirm: {
      marginXS: tVars['spacing-sm'],
    },

    Pagination: {
      borderRadius: tVars['border-radius-sm'],
      marginXS: 6,
    },

    Menu: {
      itemHeight: 32,
      itemHoverBg: vars['color-item-hover-bg'],
      itemPaddingInline: 12,
      itemBorderRadius: tVars['border-radius'],
    },

    DatePicker: {
      controlHeight: tVars['input-height'],
      controlHeightSM: tVars['input-height-sm'],
      hoverBorderColor: vars['color-blue'],
      paddingInline: 16,
      paddingInlineSM: 16,
      activeBg: vars['input-active-bg'],
      borderRadius: tVars['border-radius'],
      // antd derives calendar cell size from controlHeightSM; pin antd defaults
      cellWidth: 36,
      cellHeight: 24,
    },

    Segmented: {
      itemSelectedBg: vars['color-blue'],
      itemSelectedColor: vars['color-white'],
      itemActiveBg: vars['color-segmented-active-bg'],
      itemHoverBg: vars['color-segmented-hover-bg'],
      borderRadius: 8,
    },

    Switch: {
      trackHeight: 28,
      handleSize: 24,
      trackMinWidth: 50,
      colorPrimary: vars['color-primary'],
      colorPrimaryHover: '#0564bd',
    },

    Table: {
      headerBorderRadius: tVars['border-radius-sm'],
      rowHoverBg: vars['color-item-hover-bg'],
      headerBg: vars['color-primary-scoutingWarmGray'],
      headerColor: vars['color-white'],
      headerSortActiveBg: vars['color-table-header-sort-active-bg'],
      headerSortHoverBg: vars['color-table-header-sort-hover-bg'],
      headerFilterHoverBg: vars['color-table-header-filter-hover-bg'],
      headerSplitColor: vars['color-table-header-split'],
      ...{
        headerIconColor: vars['color-table-header-icon'],
        headerIconHoverColor: vars['color-table-header-icon-hover'],
      },
    },
  },
};

export default sblTheme;
